import { connect } from 'react-redux';

import { ApplicationState } from '../../store';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { ScrollableContent, TopActionsAndScrollableContentContainer, TopActionButtons } from '../core/TopActionsAndScrollableContentContainer';
import { CustomerContact, Purchase } from '../../store/Models/Customers/Customer';
import { MyCustomersProps } from './MyCustomersCommon';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { SelectedInventoriesManager } from '../../store/Managers/SelectedInventoriesManager';
import { CreateReportRequest } from '../../store/Models/Api/CustomerApiRequestsAndResponses';
import { ActiveTabType } from './MyCustomerDetail';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../core/ToxnotReactModal';
import { DropdownMenu } from '../core/DropdownMenu';
import { SharedMaterialsFullCompany } from '../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { withRouter } from 'react-router';

import './MyCustomers.css';

interface MyCustomerPurchasesState {
    selectedPurchaseIds: string[];
    selectedCompanyId: string | null;
}

class MyCustomerPurchases extends ToxnotSignInRequiredComponent<MyCustomersProps, MyCustomerPurchasesState>
{
    // ===============================================================================

    constructor(props: MyCustomersProps) {
        super(props)

        this.state = {
            selectedPurchaseIds: [],
            selectedCompanyId: null
        };
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public componentDidMount() {
        const selectedInventories = SelectedInventoriesManager.getThenClearSelectedInventorieFromLocalStorage(this.getUserContextForSignedInUser().userId);

        if (selectedInventories === null) {
            return;
        }

        this.props.addCustomerPurchases(selectedInventories);

    }

    // ===============================================================================

    public renderConcreteComponent() {
        return (
            <>
                <TopActionsAndScrollableContentContainer shouldMakeTnSection={false}>
                    <TopActionButtons>
                        {this.renderTopActionButtons()}
                    </TopActionButtons>
                    <ScrollableContent>
                        {this.renderBody()}
                    </ScrollableContent>
                </TopActionsAndScrollableContentContainer>    
                <ToxnotReactModal modalId={"select-company-modal"} localizedTitle={this.localizeString("Select Brand")}>
                    <ToxnotReactModalBody>
                        <p>
                            {this.localizeString("Please select the brand to share the reports under") }
                        </p>
                        <DropdownMenu
                            options={this.props.sharedMaterialCompanies}
                            getUniqueValueForOption={(c: SharedMaterialsFullCompany) => encodeURIComponent(c.companyId)}
                            getFriendlyNameForOptions={(c: SharedMaterialsFullCompany) => c.name.emptyCoalesce(this.localizeString("Unamed Brand"))}
                            onOptionSelected={(c: SharedMaterialsFullCompany) => this.setState({selectedCompanyId: c.companyId})}
                        />
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices}>
                        <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal" onClick={e => this.shareReportsForPartsWithCustomer(this.state.selectedCompanyId)}>{this.localizeString("Next")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
                <ToxnotReactModal modalId={"insufficient-companies-found-modal"} localizedTitle={this.localizeString("No Brands Found")}>
                    <ToxnotReactModalBody>
                        <p>
                            {this.localizeString("You must have at least one brand set up in order to share reports with your customers.") }
                        </p>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices}>
                        <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal" onClick={e => this.props.history.push(ToxnotReactComponentPaths.CompanyPagesManager)}>{this.localizeString("Set Up Brands")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
            </>
        );
    }

    // ===============================================================================

    public renderTopActionButtons() {
        if (!this.props.appServices.userContext?.isEditorOrMarketingRole) {
            return null;
        }

        return (
            <div>
                <button className="btn btn-default" onClick={e => this.addPurchases_click()}><span className="glyphicon glyphicon-plus"></span> {this.localizeString("Parts")}</button>
                {" "}
                <button className="btn btn-toxnot-danger" onClick={e => this.deletePurchase_click()}><span className="glyphicon glyphicon-remove"></span> {this.localizeString("Parts")}</button>
                {" "}
                <button className="btn btn-default" onClick={e => this.shareReportsButton_click()}>{this.localizeString("Share Reports for Parts with Customer")}</button>
            </div>
        );
    }

    // ===============================================================================

    public renderBody() {
        const customerId = this.props.currentlySelectedCustomerId;

        if (customerId === null) {
            throw new Error("Customer ID was null");
        }

        return (
            <ToxnotReactTable
                localizer={this.props.appServices}
                allRowModels={this.props.customerIdsToPurchases[customerId]}
                getRowForTable={this.getRow}
                headerRow={this.getHeader()}
                enablePaging={true}
                selectingRowOptions={{
                    onRowSelected: this.purchaseSelectCallback,
                    rowModelGetId: (model: Purchase) => model.simpleInventory.inventoryId
                }}
            /> 
        ); 
    }

    // ===============================================================================

    private getRow = (model: Purchase, index: number) => {
        return {
            cols: [<a href={`/Data/Inventory?inventoryId=${model.simpleInventory.inventoryId}`} target="_blank">{model.simpleInventory.name}</a>, new Date(model.datePurchased).toLocaleDateString()]
        } as TableRow
    }

    // ===============================================================================

    private getHeader() {
        return [
            {
                value: this.localizeString("Name"),
                isSortable: true,
                getSortableValue: (model: Purchase) => model.simpleInventory.name
                
            },
            {
                value: this.localizeString("Date Purchased"),
            }
        ] as TableColumn<Purchase>[]
    }

    // ===============================================================================

    private purchaseSelectCallback = (selectedIds: { [id: string]: string }) => {
        this.setState({
            selectedPurchaseIds: Object.keys(selectedIds)
        });
    }

    // ===============================================================================

    private shareReportsForPartsWithCustomer(companyIdToUse: string | null = null) {
        if (this.state.selectedPurchaseIds.length === 0) {
            alert(this.localizeString("Please select at least one part to report on."));
            return;
        }

        if (!this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled && this.state.selectedPurchaseIds.length > 1) {
            alert(this.localizeString("Your current subscription does not allow you to create a report on more than 1 part at a time. Please select only 1 part."));
            return;
        }

        const companyName = CustomerStateController.getCurrentlySelectedCustomer(this.props).companyName;
        const contacts = CustomerStateController.getCurrentlySelectedCustomer(this.props).customerContacts;

        if (companyName === null || contacts.length === 0) {
            alert(this.localizeString("You must provide a company name and at least one valid contact for this customer."));
            return;
        }

        // TODO: BH SGLS THE USER NEEDS TO SELECT THE CONTACT

        if (contacts[0].email === "") {
            alert(this.localizeString("One of this customer's contacts does not have a valid email address."));
            return;
        }

        if (companyIdToUse === null) {
            if (this.props.sharedMaterialCompanies.length === 0) {
                $('#insufficient-companies-found-modal').modal('show');
                return;
            }
            else if (this.props.sharedMaterialCompanies.length === 1) {
                companyIdToUse = this.props.sharedMaterialCompanies[0].companyId
            }
            else {
                $('#select-company-modal').modal('show');
                return;
            }
        }
        
        const materialNamesToUse = this.props.customerIdsToPurchases[CustomerStateController.getCurrentlySelectedCustomer(this.props).customerId].filter(i => this.state.selectedPurchaseIds.includes(i.simpleInventory.inventoryId)).map(i => i.simpleInventory.name);

        const request: CreateReportRequest = {
            ownerIdReceivingRequest: this.getUserContextForSignedInUser().teamOwnerId,
            companyIdReceivingRequest: companyIdToUse,
            companyName: companyName,
            contactName: contacts[0].name,
            contactEmail: contacts[0].email,
            factoryName: "",
            materialNames: materialNamesToUse,
            openRequest: false,
            reportTypes: [],
            reportTypesAndRegulationsToSpecificInfo: {},
            shouldNotifyAndCreateTaskForRequest: false // the user is proactively responding the a customer, no need for email/task
        }

        this.props.setRequestForCreateRequest(request);
        $("#select-report-type-modal").modal("show");
    }

    // ===============================================================================
    //
    // UI Events
    //

    private addPurchases_click = () => {
        const customer = CustomerStateController.getCurrentlySelectedCustomer(this.props);
        localStorage.setItem("customer-last-active-tab", ActiveTabType.Purchases);
        localStorage.removeItem("select-inventories-action");
        SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Select Products and Materials for") + " " + customer.companyName ?? this.localizeString("Unknown"));
    }

    // ===============================================================================

    private deletePurchase_click = () => {
        if (this.state.selectedPurchaseIds.length === 0) {
            alert(this.localizeString("Please select at least one purchase."))
            return;
        }

        this.props.deletePurchases(this.state.selectedPurchaseIds);
    }

    // ===============================================================================

    private shareReportsButton_click = () => {
        this.shareReportsForPartsWithCustomer();
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(withRouter(MyCustomerPurchases as any));